import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isElectron from 'is-electron';
import { deleteBubbleId } from '../../../utils/bubbleEntranceStorage';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import { buildLoginWebUrl } from '../../../utils/loginWeb';
import {
  selectActiveTenantId,
  selectActiveTenantSession,
  selectAllTenantSessions,
  selectInactiveAlarmCount,
  selectInactiveSessionsWithIndicator,
} from '../OrganizationsSlice';
import { StackedAvatars } from '../../../components/StackedAvatars/StackedAvatars';
import { getIndicatorType, type IndicatorType } from '../OrganizationsSlice/indicators';
import { switchActiveOrganization } from '../OrganizationsSlice/switchActiveOrganization';
import { beginAddOrganizationFlow } from '../OrganizationsSlice/beginAddOrganization';
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron-down.svg';
import { showAppToast } from '../../../components/AppToast/showAppToast';
import { SessionIndicator } from './SessionIndicator';
import { getLwtAlarmData } from '../../LoneWorkerTimer/loneWorkerTimerSlice';
import {
  SAddOrgIcon,
  SAddOrgRow,
  SCheckBubble,
  SCheckBubbleSlot,
  SChevron,
  SContainer,
  SDivider,
  SIndicatorStack,
  SMenu,
  SMenuHeader,
  SMenuItem,
  SMenuItemEmail,
  SMenuItemLabel,
  SMenuItemName,
  SName,
  SPillButton,
} from './TenantDropdown.styles';

const ROW_INDICATOR_A11Y_KEY: Record<IndicatorType, string> = {
  alarm: 'tenant_dropdown_alarm_active_a11y',
  imOk: 'tenant_dropdown_im_ok_active_a11y',
  message: 'tenant_dropdown_unread_active_a11y',
};

/** How many tenant avatars to surface in the pill before falling back to a count-only bubble. */
const MAX_VISIBLE_INDICATOR_AVATARS = 3;

const STACK_BUBBLE_ID = 'stack-bubble:pill';

export const TenantDropdown = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const activeSession = useAppSelector(selectActiveTenantSession);
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const allSessions = useAppSelector(selectAllTenantSessions);
  const indicatorSessions = useAppSelector(selectInactiveSessionsWithIndicator);
  const inactiveAlarmCount = useAppSelector(selectInactiveAlarmCount);
  const loneWorkerAlarmData = useAppSelector(getLwtAlarmData);

  const [expanded, setExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasPendingSwitchRef = useRef(false);

  const featureEnabled = isMultiOrgFeatureEnabled();

  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const collapse = useCallback(() => {
    if (reduceMotion) {
      setExpanded(false);
    } else {
      setIsClosing(true);
    }
  }, [reduceMotion]);

  const handleMenuAnimationEnd = useCallback(() => {
    if (isClosing) {
      setIsClosing(false);
      setExpanded(false);
    }
  }, [isClosing]);

  useEffect(() => {
    if (indicatorSessions.length === 0) {
      deleteBubbleId(STACK_BUBBLE_ID);
    }
  }, [indicatorSessions.length]);

  useEffect(() => {
    if (!expanded) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (event.target instanceof Node && containerRef.current.contains(event.target)) return;
      collapse();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') collapse();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [expanded, collapse]);

  const handleToggle = useCallback(() => {
    if (busy) return;
    if (expanded) {
      collapse();
    } else {
      setExpanded(true);
    }
  }, [busy, expanded, collapse]);

  const handleSwitch = useCallback(
    async (tenantId: number) => {
      collapse();
      if (busy || tenantId === activeTenantId) return;
      if (hasPendingSwitchRef.current) return;

      if (loneWorkerAlarmData) {
        showAppToast({
          variant: 'error',
          message: t('tenant_session_switch_error_lwt_active'),
        });
        return;
      }

      // Close the menu immediately for visual feedback. On web the menu has no
      // exit animation, so the overlay (mounted via the flag dispatch inside the
      // thunk) covers in the same React render batch — no uncovered gap.
      setExpanded(false);
      setBusy(true);
      hasPendingSwitchRef.current = true;
      try {
        const result = await dispatch(switchActiveOrganization(tenantId));
        if (!result.ok && !result.hasAlertedUser) {
          showAppToast({
            variant: 'error',
            message: t('error_try_again'),
          });
        }
      } catch (error: unknown) {
        console.error('[TenantDropdown] tenant switch failed unexpectedly', error);
      } finally {
        hasPendingSwitchRef.current = false;
        setBusy(false);
      }
    },
    [activeTenantId, busy, collapse, dispatch, loneWorkerAlarmData, t]
  );

  const handleAddTenantSession = useCallback(() => {
    collapse();

    if (loneWorkerAlarmData) {
      showAppToast({
        variant: 'error',
        message: t('tenant_session_switch_error_lwt_active'),
      });
      return;
    }

    dispatch(beginAddOrganizationFlow());
    // Match cosafe-app: go straight to the ULF without an intermediate screen.
    // `beginAddOrganizationFlow` has already set the session marker so the
    // return-side loader can distinguish add-org from a normal login.
    const loginWebUrl = buildLoginWebUrl();
    if (isElectron()) {
      window.electronApi?.sendLoginOpen(loginWebUrl);
    } else {
      window.location.href = loginWebUrl;
    }
  }, [collapse, dispatch, loneWorkerAlarmData, t]);

  if (!featureEnabled) return null;
  if (!activeSession || allSessions.length === 0) return null;

  const activeName = activeSession.tenantName?.trim() || t('tenant_dropdown_header');
  const indicatorType = indicatorSessions[0] ? getIndicatorType(indicatorSessions[0]) : null;
  const pillAriaLabel =
    indicatorType === 'alarm'
      ? `${activeName}, ${t('tenant_dropdown_pill_alarm_a11y', { count: inactiveAlarmCount })}`
      : indicatorType
        ? `${activeName}, ${t('tenant_dropdown_pill_indicator_a11y')}`
        : activeName;
  const pillAriaDescription =
    indicatorType === 'alarm'
      ? t('tenant_dropdown_toggle_a11y_hint_with_alarm', { count: inactiveAlarmCount })
      : indicatorType
        ? t('tenant_dropdown_toggle_a11y_hint_with_indicator')
        : t('tenant_dropdown_toggle_a11y_hint');

  return (
    <SContainer ref={containerRef}>
      <SPillButton
        type="button"
        onClick={handleToggle}
        $expanded={expanded && !isClosing}
        $disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={expanded && !isClosing}
        aria-label={pillAriaLabel}
        title={pillAriaDescription}
        disabled={busy}
      >
        <SessionIndicator session={activeSession} size={32} showIndicator={false} />
        <SName>{activeName}</SName>
        {indicatorSessions.length > 0 && (
          <>
            <SDivider aria-hidden />
            {indicatorSessions.length === 1 ? (
              <SessionIndicator session={indicatorSessions[0]} size={24} bubbleContext="pill" />
            ) : (
              <SIndicatorStack>
                <StackedAvatars count={indicatorSessions.length} bubbleId={STACK_BUBBLE_ID}>
                  {indicatorSessions.slice(0, MAX_VISIBLE_INDICATOR_AVATARS).map(s => (
                    <SessionIndicator
                      key={s.tenantId}
                      session={s}
                      size={24}
                      showIndicator={false}
                      bordered
                    />
                  ))}
                </StackedAvatars>
              </SIndicatorStack>
            )}
          </>
        )}
        <SChevron aria-hidden $expanded={expanded && !isClosing}>
          <ChevronDown />
        </SChevron>
      </SPillButton>

      {(expanded || isClosing) && (
        <SMenu
          role="listbox"
          $isClosing={isClosing}
          onAnimationEnd={handleMenuAnimationEnd}
          aria-hidden={isClosing}
        >
          <SMenuHeader>{t('tenant_dropdown_header')}</SMenuHeader>
          {allSessions.map(session => {
            const isActive = session.tenantId === activeTenantId;
            const rowIndicatorType = isActive ? null : getIndicatorType(session);
            const rowAriaLabel = [
              session.tenantName,
              session.email || null,
              isActive ? t('tenant_dropdown_active_a11y') : null,
              rowIndicatorType ? t(ROW_INDICATOR_A11Y_KEY[rowIndicatorType]) : null,
            ]
              .filter(Boolean)
              .join(', ');
            return (
              <SMenuItem
                key={session.tenantId}
                type="button"
                role="option"
                aria-selected={isActive}
                aria-label={rowAriaLabel}
                onClick={() => handleSwitch(session.tenantId)}
                disabled={busy}
              >
                <SessionIndicator
                  session={session}
                  size={36}
                  showIndicator={!isActive}
                  bubbleContext="menu"
                />
                <SMenuItemLabel>
                  <SMenuItemName>{session.tenantName}</SMenuItemName>
                  {session.email && <SMenuItemEmail>{session.email}</SMenuItemEmail>}
                </SMenuItemLabel>
                <SCheckBubbleSlot>
                  <SCheckBubble
                    $checked={isActive}
                    aria-label={isActive ? t('tenant_dropdown_active_a11y') : undefined}
                  />
                </SCheckBubbleSlot>
              </SMenuItem>
            );
          })}

          <SAddOrgRow type="button" onClick={handleAddTenantSession} disabled={busy}>
            <SAddOrgIcon aria-hidden>+</SAddOrgIcon>
            {t('add_organization_title')}
          </SAddOrgRow>
        </SMenu>
      )}
    </SContainer>
  );
};
